import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den Vorlagen-Management-Dialog
 * (`TemplatesComponent` — Schichtplan-Wochen-Vorlagen speichern, anwenden,
 * bearbeiten, loeschen). Pattern analog zu `AUTO_SUGGEST_HELP_TOUR` und
 * `WORK_SHIFT_REPORT_HELP_TOUR`. 5 kompakte Steps — bewusst dicht statt
 * lang, damit User nicht abbricht.
 */

type TemplatesTabId = 'library' | 'apply' | 'save' | 'manage';

/**
 * Wechselt im TemplatesComponent zum Library-Tab via synthetischen Klick auf
 * den Tab-Button. Idempotent: prueft die `.active`-Klasse, klickt nur wenn
 * noetig. Notwendig fuer Welcome- und Library-Steps, damit Anchors im DOM
 * sind, falls der User vorher auf einem anderen Tab war.
 */
function switchToTemplatesTab(tabId: TemplatesTabId): void {
  // Tab-Buttons haben keine ID-Attribute — wir matchen ueber den Text-Inhalt.
  const labelMap: Record<TemplatesTabId, RegExp> = {
    library: /Bibliothek|Library/i,
    apply: /Auf Woche anwenden|Apply/i,
    save: /Aktuelle Woche speichern|Save/i,
    manage: /Bearbeiten|Manage/i,
  };
  const buttons = document.querySelectorAll<HTMLButtonElement>(
    '.tpl-rail .rail-nav .tab-btn',
  );
  for (const btn of Array.from(buttons)) {
    if (labelMap[tabId].test(btn.textContent ?? '')) {
      if (!btn.classList.contains('active')) {
        btn.click();
      }
      return;
    }
  }
}

export const TEMPLATES_HELP_TOUR: TourConfig = {
  id: TourFeature.TemplatesHelp,
  version: 1,
  title: 'TEMPLATES_HELP_TOUR_TITLE',
  description: 'TEMPLATES_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Schichtzuweisung navigieren. Der "Vorlagen"-Button
  // sitzt seit dem Toolbar-Refactor im Overflow-Menue
  // (`fc-overflowMenuButton-button`) — kein deterministischer 1-Klick-Opener.
  // Daher `route` only; User oeffnet Menue + Item selbst.
  launchContext: {
    route: '/planner',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 110,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'TEMPLATES_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert den GANZEN Dialog — Spotlight auf den Wrapper.
          // onBeforeShow stellt sicher, dass die Library aktiv ist, damit die
          // folgenden Steps ihre Anchors finden.
          anchor: AnchorId.TemplatesDialog,
          title: 'TUTORIAL.TEMPLATES.WELCOME.TITLE',
          content: 'TUTORIAL.TEMPLATES.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToTemplatesTab('library'),
        },
        {
          id: 'sidebar',
          // Sidebar deckt 4 Workflows + Schnell-Stats + Zuletzt-Verwendet ab
          // — ein Step fuer drei Sidebar-Bereiche.
          anchor: AnchorId.TemplatesSidebar,
          title: 'TUTORIAL.TEMPLATES.SIDEBAR.TITLE',
          content: 'TUTORIAL.TEMPLATES.SIDEBAR.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToTemplatesTab('library'),
        },
        {
          id: 'recommendation-kpis',
          // Popover sitzt an der KPI-Row (mittig im Content), Spotlight
          // umfasst zusaetzlich das Smart-Suggestion-Banner darueber.
          anchor: AnchorId.TemplatesKpiRow,
          additionalSpotlightAnchors: [AnchorId.TemplatesSmartBanner],
          title: 'TUTORIAL.TEMPLATES.RECOMMENDATION_KPIS.TITLE',
          content: 'TUTORIAL.TEMPLATES.RECOMMENDATION_KPIS.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToTemplatesTab('library'),
        },
        {
          id: 'library-grid',
          // Filter + Grid in einem Step. Popover an der Filter-Bar, Spotlight
          // erweitert auf das Karten-Grid darunter.
          anchor: AnchorId.TemplatesFilterBar,
          additionalSpotlightAnchors: [AnchorId.TemplatesGrid],
          title: 'TUTORIAL.TEMPLATES.LIBRARY_GRID.TITLE',
          content: 'TUTORIAL.TEMPLATES.LIBRARY_GRID.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToTemplatesTab('library'),
        },
        {
          id: 'save-cta',
          anchor: AnchorId.TemplatesSaveCta,
          title: 'TUTORIAL.TEMPLATES.SAVE_CTA.TITLE',
          content: 'TUTORIAL.TEMPLATES.SAVE_CTA.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToTemplatesTab('library'),
          // Letzter Step — kein Zurueck-Button.
          showPrevButton: false,
        },
      ],
    },
  ],
};
